import React, { useState, useEffect, useRef } from 'react';
import { Archive, X, EyeOff, CheckCircle2 } from 'lucide-react';

const MOTIVOS_FRECUENTES = ['Cliente de prueba', 'Contrato terminado', 'Base duplicada', 'Sin operación'];

// Reemplaza window.prompt: deja claro que archivar solo afecta la vista
// del ERP y que en Punto V el cliente sigue operando.
const DialogoArchivar = ({ cliente, onConfirmar, onCancel }) => {
  const [motivo, setMotivo] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const campoRef = useRef(null);

  useEffect(() => {
    campoRef.current?.focus();
    const alPresionar = (e) => { if (e.key === 'Escape' && !loading) onCancel(); };
    document.addEventListener('keydown', alPresionar);
    return () => document.removeEventListener('keydown', alPresionar);
  }, [onCancel, loading]);

  const confirmar = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await onConfirmar(motivo.trim() || null);
    } catch (err) {
      setError(err.message || 'No se pudo archivar el cliente. Intente de nuevo.');
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 backdrop-blur-[2px] flex items-center justify-center z-50 p-4"
      onClick={() => !loading && onCancel()}
    >
      <form
        onSubmit={confirmar}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="titulo-archivar"
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
      >
        <div className="px-6 pt-6 pb-4 flex items-start gap-4">
          <div className="bg-amber-100 text-amber-700 rounded-xl p-2.5 flex-shrink-0">
            <Archive size={22} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 id="titulo-archivar" className="text-lg font-semibold text-gray-900">
              Archivar {cliente.slug}
            </h3>
            <p className="text-sm text-gray-500 truncate">{cliente.duenio.nombre} · {cliente.base_datos}</p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            aria-label="Cerrar"
            className="text-gray-400 hover:text-gray-600 rounded-lg p-1 focus-visible:ring-2 focus-visible:ring-blue-500 outline-none"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-6 space-y-2 text-sm">
          <div className="flex items-start gap-2.5 text-gray-700">
            <EyeOff size={16} className="mt-0.5 text-gray-400 flex-shrink-0" />
            <span>Deja de aparecer en Activos. Lo encuentra en Archivados y puede restaurarlo cuando quiera.</span>
          </div>
          <div className="flex items-start gap-2.5 text-gray-700">
            <CheckCircle2 size={16} className="mt-0.5 text-green-600 flex-shrink-0" />
            <span>En Punto V no cambia nada: sus usuarios siguen entrando y sus máquinas siguen vendiendo.</span>
          </div>
        </div>

        <div className="px-6 pt-5">
          <label htmlFor="motivo-archivo" className="block text-sm font-medium text-gray-700 mb-2">
            Motivo <span className="font-normal text-gray-400">(opcional)</span>
          </label>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {MOTIVOS_FRECUENTES.map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => { setMotivo(m); campoRef.current?.focus(); }}
                className={`px-2.5 py-1 rounded-full text-xs border transition-colors outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
                  motivo === m
                    ? 'bg-blue-50 border-blue-300 text-blue-700'
                    : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                {m}
              </button>
            ))}
          </div>
          <textarea
            id="motivo-archivo"
            ref={campoRef}
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            rows={2}
            maxLength={200}
            placeholder="Por qué se archiva este cliente"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
          />
        </div>

        {error && (
          <div className="mx-6 mt-3 bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>
        )}

        <div className="px-6 py-4 mt-5 bg-gray-50 border-t border-gray-100 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="px-4 py-2 text-sm text-gray-700 rounded-lg hover:bg-gray-100 outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50 flex items-center gap-2 outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
          >
            <Archive size={16} />
            {loading ? 'Archivando…' : 'Archivar cliente'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default DialogoArchivar;
